import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';

const Step7OptionalFields = ({ formData, onChange }) => {
  const [openSections, setOpenSections] = useState({
    assets: false,
    literacy: false,
    family: false,
    technology: false,
    other: false
  });

  const toggleSection = (section) => {
    setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Optional Additional Information</h3>
        <p className="text-sm text-gray-600">
          These fields are completely optional but can help improve the accuracy of your credit assessment. Expand any section to provide additional details.
        </p>
      </div>

      {/* Additional Assets Section */}
      <Collapsible open={openSections.assets} onOpenChange={() => toggleSection('assets')}>
        <CollapsibleTrigger className="w-full">
          <div className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
            <div className="text-left">
              <h4 className="font-medium text-gray-900">Additional Assets</h4>
              <p className="text-sm text-gray-500">Livestock, equipment, and land details</p>
            </div>
            <ChevronDown className={`h-5 w-5 text-gray-500 transition-transform ${openSections.assets ? 'rotate-180' : ''}`} />
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-2">
          <div className="p-4 bg-gray-50 rounded-lg space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="livestock_value">Livestock Value (in lakhs)</Label>
                <Input
                  id="livestock_value"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.livestock_value || ''}
                  onChange={(e) => onChange('livestock_value', parseFloat(e.target.value) || '')}
                  placeholder="Value of livestock in lakhs"
                />
              </div>
              {/* Land Fragmentation Index and Crop Diversification Index removed - now auto-calculated in Agriculture Step */}
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Financial Literacy Section */}
      <Collapsible open={openSections.literacy} onOpenChange={() => toggleSection('literacy')}>
        <CollapsibleTrigger className="w-full">
          <div className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
            <div className="text-left">
              <h4 className="font-medium text-gray-900">Financial Literacy</h4>
              <p className="text-sm text-gray-500">Understanding of financial concepts</p>
            </div>
            <ChevronDown className={`h-5 w-5 text-gray-500 transition-transform ${openSections.literacy ? 'rotate-180' : ''}`} />
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-2">
          <div className="p-4 bg-gray-50 rounded-lg space-y-4">
            <div className="space-y-2">
              <Label htmlFor="financial_literacy_score">Financial Literacy Score (0-100)</Label>
              <Input
                id="financial_literacy_score"
                type="number"
                min="0"
                max="100"
                value={formData.financial_literacy_score || ''}
                onChange={(e) => onChange('financial_literacy_score', parseFloat(e.target.value) || '')}
                placeholder="Self-assessed score"
              />
              <p className="text-xs text-gray-500">Rate your understanding of loans, interest, and financial planning</p>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Family Support Section */}
      <Collapsible open={openSections.family} onOpenChange={() => toggleSection('family')}>
        <CollapsibleTrigger className="w-full">
          <div className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
            <div className="text-left">
              <h4 className="font-medium text-gray-900">Family Support</h4>
              <p className="text-sm text-gray-500">Family involvement in farming</p>
            </div>
            <ChevronDown className={`h-5 w-5 text-gray-500 transition-transform ${openSections.family ? 'rotate-180' : ''}`} />
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-2">
          <div className="p-4 bg-gray-50 rounded-lg space-y-4">
            <div className="space-y-2">
              <Label htmlFor="family_support_score">Family Support</Label>
              <Input
                id="family_support_score"
                type="number"
                min="0"
                value={formData.family_support_score || ''}
                onChange={(e) => onChange('family_support_score', parseInt(e.target.value) || '')}
                placeholder="Number of adult household members"
              />
              <p className="text-xs text-gray-500">Number of adult household members helping in farming (excluding applicant)</p>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Technology & Other Section */}
      <Collapsible open={openSections.technology} onOpenChange={() => toggleSection('technology')}>
        <CollapsibleTrigger className="w-full">
          <div className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
            <div className="text-left">
              <h4 className="font-medium text-gray-900">Technology & Training</h4>
              <p className="text-sm text-gray-500">Technology adoption and training programs</p>
            </div>
            <ChevronDown className={`h-5 w-5 text-gray-500 transition-transform ${openSections.technology ? 'rotate-180' : ''}`} />
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-2">
          <div className="p-4 bg-gray-50 rounded-lg space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="technology_adoption_score">Technology Adoption Score (0-10)</Label>
                <Input
                  id="technology_adoption_score"
                  type="number"
                  min="0"
                  max="10"
                  value={formData.technology_adoption_score || ''}
                  onChange={(e) => onChange('technology_adoption_score', parseFloat(e.target.value) || '')}
                  placeholder="Use of modern farming tech"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="training_program_participation">Training Programs Attended</Label>
                <Input
                  id="training_program_participation"
                  type="number"
                  min="0"
                  value={formData.training_program_participation || ''}
                  onChange={(e) => onChange('training_program_participation', parseInt(e.target.value) || '')}
                  placeholder="Number of programs"
                />
              </div>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Information Box */}
      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
        <h4 className="font-medium text-blue-900 mb-2">About Optional Fields</h4>
        <p className="text-sm text-blue-800">
          These fields are completely optional and can be filled later. However, providing more information helps create a more accurate credit assessment and may improve your credit score.
        </p>
      </div>
    </div>
  );
};

export default Step7OptionalFields;
